import React, { useCallback, useState } from 'react';
import { Button, Select, Tooltip, Typography } from 'antd';
import * as QueryString from 'query-string';
import { useHistory, useLocation } from 'react-router';
import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    CaretDownFilled,
    CaretDownOutlined,
    PartitionOutlined,
    ReloadOutlined,
    SubnodeOutlined,
} from '@ant-design/icons';
import styled from 'styled-components/macro';

import { useEntityData } from '../../EntityContext';
import TabToolbar from '../../components/styled/TabToolbar';
import { getEntityPath } from '../../containers/profile/utils';
import { useEntityRegistry } from '../../../../useEntityRegistry';
import { ImpactAnalysis } from './ImpactAnalysis';
import { EntityType, LineageDirection } from '../../../../../types.generated';
import { generateSchemaFieldUrnWithoutEncoding } from './utils';
import { downgradeV2FieldPath } from '../../../dataset/profile/schema/utils/utils';
import ColumnsLineageSelect from './ColumnLineageSelect';
import { LineageTabContext } from './LineageTabContext';
import ManageLineageMenu from '../../../../lineage/manage/ManageLineageMenu';
import LineageTabTimeSelector from './LineageTabTimeSelector';
import { useGetLineageTimeParams } from '../../../../lineage/utils/useGetLineageTimeParams';
import { useTranslation } from '../../../../../i18n/useTranslation';
import { ANTD_GRAY } from '../../constants';
import { useRbac } from '../../useRbac';

const StyledTabToolbar = styled(TabToolbar)`
    justify-content: space-between;
    z-index: 2;
`;
const LineageTabWrapper = styled.div`
    background-color: #ffffff;
`;

const LeftButtonsWrapper = styled.div`
    align-items: center;
    display: flex;
`;

const RightButtonsWrapper = styled.div`
    align-items: center;
    display: flex;
`;

const ManageLineageIcon = styled(SubnodeOutlined)`
    &&& {
        margin-right: -2px;
    }
`;

const StyledCaretDown = styled(CaretDownFilled)`
    &&& {
        font-size: 14px;
        margin-left: 4px;
    }
`;

const StyledSelect = styled(Select)`
    b {
        margin-right: 4px;
    }
    &:hover {
        background-color: ${ANTD_GRAY[2]};
    }
`;

const RefreshCacheButton = styled(Button)`
    margin-left: 8px;
`;

export const LineageTab = ({
    properties = { defaultDirection: LineageDirection.Downstream },
}: {
    properties?: { defaultDirection: LineageDirection };
}) => {
    const { t } = useTranslation();
    const { urn, entityType, entityData } = useEntityData();
    const history = useHistory();
    const location = useLocation();
    const entityRegistry = useEntityRegistry();
    const params = QueryString.parse(location.search, { arrayFormat: 'comma' });
    const [lineageDirection, setLineageDirection] = useState<LineageDirection>(properties.defaultDirection);
    const [selectedColumn, setSelectedColumn] = useState<string | undefined>(params?.column as string);
    const [isColumnLevelLineage, setIsColumnLevelLineage] = useState(!!params?.column);
    const [skipCache, setSkipCache] = useState(false);
    const { startTimeMillis, endTimeMillis } = useGetLineageTimeParams();
    // Only disable when explicitly set to true (null and false are treated as enabled)
    const isReadOnly = entityData?.readOnly?.readOnly === true;

    function resetShouldRefetch() {
        setSkipCache(false);
    }

    const routeToLineage = useCallback(() => {
        history.push(
            getEntityPath(entityType, urn, entityRegistry, true, false, 'Lineage', {
                start_time_millis: startTimeMillis,
                end_time_millis: endTimeMillis,
            }),
        );
    }, [history, entityType, urn, entityRegistry, startTimeMillis, endTimeMillis]);

    const selectedV1FieldPath = downgradeV2FieldPath(selectedColumn) || '';
    const selectedColumnUrn = generateSchemaFieldUrnWithoutEncoding(selectedV1FieldPath, urn);
    const impactAnalysisUrn = isColumnLevelLineage && selectedColumnUrn ? selectedColumnUrn : urn;

    const { canEditLineage: checkCanEditLineage } = useRbac();
    const canEditLineage = checkCanEditLineage(entityType as EntityType) && !isReadOnly;

    const directionOptions = [
        {
            label: (
                <span data-testid="lineage-tab-direction-select-option-downstream" aria-label="Downstream">
                    <ArrowDownOutlined style={{ marginRight: 4 }} />
                    <b>{t('Downstream')}</b>
                </span>
            ),
            value: LineageDirection.Downstream,
            tabIndex: 0,
        },
        {
            label: (
                <span data-testid="lineage-tab-direction-select-option-upstream" aria-label="Upstream">
                    <ArrowUpOutlined style={{ marginRight: 4 }} />
                    <b>{t('Upstream')}</b>
                </span>
            ),
            value: LineageDirection.Upstream,
            tabIndex: 0,
        },
    ];

    return (
        <LineageTabWrapper>
            <StyledTabToolbar>
                <LeftButtonsWrapper>
                    <Tooltip title={isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined}>
                        <ManageLineageMenu
                            entityUrn={urn}
                            refetchEntity={() => setSkipCache(true)}
                            setUpdatedLineages={() => {}}
                            menuIcon={
                                <Button type="text" aria-label={t('Edit')} disabled={isReadOnly}>
                                    <ManageLineageIcon aria-hidden="true" />
                                    <Typography.Text>
                                        <b>{t('Edit')}</b>
                                    </Typography.Text>
                                    <StyledCaretDown />
                                </Button>
                            }
                            showLoading
                            entityType={entityType}
                            entityPlatform={entityData?.platform?.name}
                            canEditLineage={canEditLineage}
                            disableDropdown={!canEditLineage}
                        />
                    </Tooltip>
                    <Button type="text" onClick={routeToLineage} aria-label={t('Visualize Lineage')}>
                        <PartitionOutlined />
                        <Typography.Text>
                            <b>{t('Visualize Lineage')}</b>
                        </Typography.Text>
                    </Button>
                </LeftButtonsWrapper>
                <RightButtonsWrapper className="f-layout-wrap-xs f-justify-between-xs f-gap-2">
                    <StyledSelect
                        bordered={false}
                        value={lineageDirection}
                        options={directionOptions}
                        aria-label={t('Lineage Direction')}
                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                        onChange={(value) => setLineageDirection(value as LineageDirection)}
                        suffixIcon={<CaretDownOutlined style={{ color: 'black' }} />}
                        data-testid="lineage-tab-direction-select"
                    />
                    <ColumnsLineageSelect
                        selectedColumn={selectedColumn}
                        isColumnLevelLineage={isColumnLevelLineage}
                        setSelectedColumn={setSelectedColumn}
                        setIsColumnLevelLineage={setIsColumnLevelLineage}
                    />
                    <LineageTabTimeSelector />
                    <Tooltip title={t('Click to refresh data')}>
                        <RefreshCacheButton type="text" onClick={() => setSkipCache(true)} aria-label={t('Refresh')}>
                            <ReloadOutlined aria-hidden="true" />
                            <Typography.Text>
                                <b>{t('Refresh')}</b>
                            </Typography.Text>
                        </RefreshCacheButton>
                    </Tooltip>
                </RightButtonsWrapper>
            </StyledTabToolbar>
            <LineageTabContext.Provider value={{ isColumnLevelLineage, selectedColumn, lineageDirection }}>
                <ImpactAnalysis
                    urn={impactAnalysisUrn}
                    direction={lineageDirection as LineageDirection}
                    startTimeMillis={startTimeMillis}
                    endTimeMillis={endTimeMillis}
                    skipCache={skipCache}
                    setSkipCache={setSkipCache}
                    resetShouldRefetch={resetShouldRefetch}
                />
            </LineageTabContext.Provider>
        </LineageTabWrapper>
    );
};
